import { ExclamationCircleFilled } from '@ant-design/icons'
import { faPills, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal as AntModal, Button, Card, Col, Modal, Row, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import PrevNextFloatingButtons from '../../../Reusable Components/Buttons/PrevNextFloatingButtons'
import DatePicker from '../../../Reusable Components/DatePicker'
import useGeneralMeds from '../../hooks/useGeneralMeds'
import '../style/MobGeneralMeds.css'
import MobSectionHeader from './MobSectionHeader'

const MobGeneralMeds = ({ clientForm, data, setFacet, setSaving }) => {
  const {
    medsList,
    nappiData,
    nappiLoading,
    setNappiSearchFilter,
    handleAddMedication,
    handleUpdateMedication,
    handleRemoveMedication,
    handleSubmit,
    showMedicationModal,
    setShowMedicationModal,
    editingMedication,
    setEditingMedication,
    tempMedDates,
    setTempMedDates
  } = useGeneralMeds({ clientForm, setFacet, setSaving })

  const [showMedStartDatePicker, setShowMedStartDatePicker] = useState(false)
  const [showMedEndDatePicker, setShowMedEndDatePicker] = useState(false)

  const styles = {
    header: {
      fontSize: '20px',
      fontWeight: 600,
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    addButton: {
      background: '#00A6E3',
      borderRadius: '8px',
      border: 'none',
      height: '48px',
      fontSize: '16px',
      fontWeight: 500
    },
    medicationCard: {
      borderRadius: '12px',
      border: 'none',
      marginBottom: '12px'
    },
    medicationIcon: {
      color: '#00A6E3',
      fontSize: '24px',
      marginTop: '4px'
    },
    medicationName: {
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    dateText: {
      color: '#8F9BB3',
      margin: '4px 0 0 0',
      fontSize: '14px'
    },
    removeButton: {
      border: '1px dashed #FF4D4F',
      color: '#FF4D4F',
      background: 'transparent',
      borderRadius: '8px',
      height: '44px',
      width: '100%'
    }
  }

  const showDeleteMedicationConfirm = (medication, index) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div style={{ color: '#8F9BB3', fontSize: '14px', lineHeight: '1.5' }}>
          <p>Are you sure you want to remove {medication.med.name}?</p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: {
        danger: true
      },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(index)
      },
      className: 'dark-modal',
      centered: true
    })
  }

  const { Text } = Typography

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <MobSectionHeader icon={faPills} title='General Medications' subtitle='Update your current medications' />
      </Col>
      <Col span={24}>
        <div
          style={{
            padding: '12px 16px',
            background: 'rgba(0, 166, 227, 0.1)',
            borderRadius: '8px',
            marginBottom: '12px',
            color: '#555'
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Why is this important?</p>
          <p style={{ margin: '0', fontSize: '14px' }}>
            List all medications not linked to chronic illnesses. This helps your doctor avoid prescribing medications
            with potential interactions or adverse effects due to conflicting active ingredients.
          </p>
        </div>
      </Col>
      <Col span={24}>
        <Button
          type='primary'
          onClick={() => setShowMedicationModal(true)}
          icon={<FontAwesomeIcon icon={faPlus} />}
          block
          style={styles.addButton}
        >
          Add Medication
        </Button>
      </Col>

      <Col span={24}>
        {medsList?.length === 0 ? (
          <div className='empty-state' style={{ textAlign: 'center', padding: '40px 20px' }}>
            <FontAwesomeIcon
              icon={faPills}
              className='empty-icon'
              style={{ fontSize: '32px', marginBottom: '12px', opacity: '0.5' }}
            />
            <p style={{ fontSize: '15px', margin: '0' }}>No medications added yet</p>
          </div>
        ) : (
          <Space direction='vertical' style={{ width: '100%' }}>
            {medsList?.map((medication, index) => (
              <Card key={index} style={styles.medicationCard} bodyStyle={{ padding: '16px' }}>
                <Row gutter={[0, 12]}>
                  <Col span={24}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div
                        style={styles.medicationName}
                        onClick={() => {
                          setEditingMedication({
                            ...medication,
                            index
                          })
                          setTempMedDates({
                            startDate: medication.startDate,
                            endDate: medication.endDate
                          })
                          setShowMedicationModal(true)
                        }}
                      >
                        <Space direction='vertical' size={0}>
                          <Text strong>
                            {medication.med.name
                              .split(' ')
                              .map((word) =>
                                word.length <= 2
                                  ? word.toUpperCase()
                                  : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                              )
                              .join(' ')}
                          </Text>
                          <Text type='secondary' style={{ fontSize: '12px' }}>
                            {[
                              medication.med.strengthMetric1,
                              medication.med.unitOfMeasure1,
                              medication.med.presentationCode
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </Text>
                        </Space>
                      </div>
                      <Button
                        type='text'
                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#FF4D4F' }} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          showDeleteMedicationConfirm(medication, index)
                        }}
                        style={{ padding: '4px', height: 'auto' }}
                      />
                    </div>
                    <div style={styles.dateText}>
                      <p>Started: {dayjs(medication.startDate).format('DD MMM YYYY')}</p>
                      <p>
                        {medication.endDate && <span>Ended: {dayjs(medication.endDate).format('DD MMM YYYY')}</span>}
                        {!medication.endDate && <span> - Current</span>}
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        )}
      </Col>

      <Modal
        title={
          <div>
            <div>{editingMedication ? 'Edit Medication' : 'Add Medication'}</div>
            <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px' }}>
              {editingMedication ? 'Update medication dates' : 'Search our database of medications'}
            </div>
          </div>
        }
        open={showMedicationModal}
        onCancel={() => {
          setShowMedicationModal(false)
          setEditingMedication(null)
          setTempMedDates({ startDate: null, endDate: null })
        }}
        footer={
          <Button
            type='primary'
            disabled={!tempMedDates.startDate || (!editingMedication && !nappiData.length)}
            onClick={() => {
              if (editingMedication) {
                handleUpdateMedication(editingMedication.index, tempMedDates)
              } else {
                handleAddMedication(nappiData[0], tempMedDates)
              }
            }}
          >
            {editingMedication ? 'Update' : 'Add'} Medication
          </Button>
        }
        className='dark-modal'
      >
        {editingMedication ? (
          <>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 500 }}>
                {editingMedication.med.name
                  .split(' ')
                  .map((word) =>
                    word.length <= 2 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(' ')}
                {editingMedication.med.dosageForm && ` (${editingMedication.med.dosageForm})`}
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '16px', color: '#8F9BB3' }}>
              <p>Search for your medication by typing its name or brand.</p>
              <p>• Enter the medication name or active ingredient</p>
              <p>• Select from the matching suggestions</p>
            </div>
            <Select
              className='dark-modal-select'
              showSearch
              placeholder='E.g. Type "Metformin", "Ventolin", "Aspirin" ...'
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              onSearch={setNappiSearchFilter}
              notFoundContent={nappiLoading ? 'Searching...' : 'No results found. Try different keywords.'}
              style={{ width: '100%' }}
              options={nappiData.map((medData) => ({
                search: `${medData.name || ''} ${medData.strengthMetric1 || ''} ${medData.unitOfMeasure1 || ''} ${
                  medData.presentationCode || ''
                } - ${medData.standardPacksize || ''}`,
                label: (
                  <Space direction='vertical' size={0}>
                    <Text strong>{medData.name || ''}</Text>
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      {[medData.strengthMetric1, medData.unitOfMeasure1, medData.presentationCode]
                        .filter(Boolean)
                        .join(' • ')}
                    </Text>
                  </Space>
                ),
                value: JSON.stringify({
                  code: medData.code,
                  name: medData.name
                })
              }))}
            />
          </>
        )}

        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#8F9BB3', marginBottom: '8px' }}>Start Date</div>
          <div
            onClick={() => setShowMedStartDatePicker(true)}
            style={{
              border: '1px solid #8F9BB3',
              padding: '8px 12px',
              borderRadius: '8px',
              color: tempMedDates.startDate ? '#000000' : '#8F9BB3',
              cursor: 'pointer'
            }}
          >
            {tempMedDates.startDate ? dayjs(tempMedDates.startDate).format('DD MMM YYYY') : 'Select start date'}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#8F9BB3', marginBottom: '8px' }}>End Date (Optional)</div>
          <div
            onClick={() => setShowMedEndDatePicker(true)}
            style={{
              border: '1px solid #8F9BB3',
              padding: '8px 12px',
              borderRadius: '8px',
              color: tempMedDates.endDate ? '#000000' : '#8F9BB3',
              cursor: 'pointer'
            }}
          >
            {tempMedDates.endDate ? dayjs(tempMedDates.endDate).format('DD MMM YYYY') : 'Select end date'}
          </div>
        </div>

        <DatePicker
          showDatePicker={showMedStartDatePicker}
          setShowDatePicker={setShowMedStartDatePicker}
          pickerValue={tempMedDates.startDate ? dayjs(tempMedDates.startDate) : null}
          setPickerValue={(date) => setTempMedDates((prev) => ({ ...prev, startDate: date }))}
          handleDateSelect={(date) => {
            setTempMedDates((prev) => ({ ...prev, startDate: date }))
            setShowMedStartDatePicker(false)
          }}
        />

        <DatePicker
          showDatePicker={showMedEndDatePicker}
          setShowDatePicker={setShowMedEndDatePicker}
          pickerValue={tempMedDates.endDate ? dayjs(tempMedDates.endDate) : null}
          setPickerValue={(date) => setTempMedDates((prev) => ({ ...prev, endDate: date }))}
          handleDateSelect={(date) => {
            setTempMedDates((prev) => ({ ...prev, endDate: date }))
            setShowMedEndDatePicker(false)
          }}
        />
      </Modal>

      <PrevNextFloatingButtons
        onPrev={() => {
          clientForm.resetFields()
          setFacet(null)
          window.scrollTo(0, 0)
        }}
        onNext={handleSubmit}
        nextText='Save'
        prevText='Back'
      />
    </Row>
  )
}

export default MobGeneralMeds
